import i18n from '@/i18n';
import { useEnvStore } from '@/stores/envStore';
import type { Restaurant } from '@/types/restaurants';
import type { ToastServiceMethods } from 'primevue/toastservice';
import { useFetch } from './useFetch';

const { fetchData } = useFetch();
const { t } = i18n.global;

export function useProfileRestaurant(toast: ToastServiceMethods, restaurantId: string) {
  async function updateRestaurantProperty(propertyName: string, value: string | number): Promise<Restaurant | null> {
    const url = useEnvStore().apiUrl + '/restaurants/' + restaurantId;
    const { error, data } = await fetchData(url, 'PATCH', { [propertyName]: value });

    if (error) {
      console.log(error);
      if (error.details.message) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: error.details.message,
          life: 3000,
        });
      } else {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('profile.profile.restaurantTab.updateError'),
          life: 3000,
        });
      }
      return null;
    }
    // toast.add({
    //   severity: 'success',
    //   summary: t('common.success'),
    //   detail: t('profile.profile.restaurantTab.updateSuccess'),
    //   life: 3000,
    // })

    return data as Restaurant;
  }

  async function updateRestaurantAttributes({
    attributeIds,
    isRestaurationType = false,
    toggleAll = false,
  }: {
    attributeIds: number[];
    isRestaurationType?: boolean;
    toggleAll?: boolean;
  }): Promise<Restaurant | null> {
    const url = useEnvStore().apiUrl + '/restaurants/' + restaurantId;

    const body = isRestaurationType ? { restaurationTypes: attributeIds } : { attributes: attributeIds, toggleAll };

    console.log(body);

    const { error, data } = await fetchData(url, 'PATCH', body);

    if (error) {
      console.log(error);
      if (error.details.message) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: error.details.message,
          life: 3000,
        });
      } else {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('profile.profile.restaurantTab.updateError'),
          life: 3000,
        });
      }
      return null;
    }
    // toast.add({
    //   severity: 'success',
    //   summary: t('common.success'),
    //   detail: t('profile.profile.restaurantTab.updateSuccess'),
    //   life: 3000,
    // })

    return data as Restaurant;
  }

  async function postRestaurantAddress(address: { address_line_1: string; city: string; postal_code: number | string }): Promise<Restaurant | null> {
    const url = useEnvStore().apiUrl + '/restaurants/' + restaurantId + '/address';
    const { error, data } = await fetchData(url, 'POST', address);

    if (error) {
      console.log(error);
      if (error.details?.message) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: error.details.message,
          life: 3000,
        });
      } else {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('profile.profile.restaurantTab.updateError'),
          life: 3000,
        });
      }
      return null;
    }

    return data as Restaurant;
  }

  async function updateRestaurantShiftSlot(
    slotId: number,
    shiftData: { name: string; start_time: string | null; end_time: string | null },
  ): Promise<boolean> {
    const url = `${useEnvStore().apiUrl}/restaurants/${restaurantId}/shift-slots/${slotId}`;
    const { error, data } = await fetchData(url, 'PATCH', {
      name: shiftData.name,
      start_time: shiftData.start_time || null,
      end_time: shiftData.end_time || null,
    });

    if (error || !data) {
      console.error('Error updating shift slot:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('profile.profile.restaurantTab.shiftSlots.updateError'), // Assuming you have this translation key
        life: 3000,
      });
      return false;
    }

    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('profile.profile.restaurantTab.shiftSlots.updateSuccess'), // Assuming you have this translation key
      life: 3000,
    });
    return true;
  }

  return { updateRestaurantProperty, updateRestaurantAttributes, postRestaurantAddress, updateRestaurantShiftSlot };
}
